import React, { useState } from 'react';
import { 
  MessageCircleQuestion, 
  Brain, 
  Target, 
  Puzzle,
  UserCheck
} from 'lucide-react';
import ToolDescription from './ToolDescription';
import { getTeamMembers } from '../services/teamService';

const IceBreakers: React.FC = () => {
  const [activeGame, setActiveGame] = useState('trivia');
  const [question, setQuestion] = useState('');

  const games = [
    { id: 'trivia', name: 'Tech Questions', icon: Brain },
    { id: 'questions', name: 'Personal Questions', icon: MessageCircleQuestion },
    { id: 'wordchain', name: 'Word Chain', icon: Puzzle },
    { id: 'wouldyou', name: 'Would You Rather', icon: Target },
  ];

  const triviaQuestions = [
    // Technical Questions
    "What's your favorite coding language and why?",
    "If you could master any technology instantly, what would it be?",
    "What's the most interesting bug you've ever encountered?",
    "What's your dream project to work on?",
    "What's your preferred working environment?",
    "Which software tool can't you live without?",
    "What's your favorite keyboard shortcut?",
    "What's the best piece of coding advice you've received?",
    "What technology do you think will be obsolete in 10 years?",
    "What's your debugging strategy?",
    "Tabs or spaces? Defend your answer!",
    "What's your favorite tech stack?",
    "What's the most valuable lesson you've learned in your career?",
    "Which tech conference would you love to attend?",
    "What's your preferred code review process?",
    "What's your opinion on pair programming?",
    "What's your favorite design pattern?",
    "How do you stay updated with tech trends?",
    "What's your most used Git command?",
    "What's your favorite VS Code extension?",
    // Additional Tech Questions
    "What was your first programming language?",
    "What's your most controversial tech opinion?",
    "What's the worst legacy system you've had to work with?",
    "What's your preferred project management methodology?",
    "Dark mode or light mode for coding?",
    "Desktop or laptop for development?",
    "What's your favorite algorithm?",
    "What tech skill do you think everyone should learn?",
    "What's your go-to technique for solving complex problems?",
    "What non-technical skill has helped your tech career the most?",
    "What's your favorite Easter egg in software?",
    "What's the oldest technology you still use regularly?",
    "What's your approach to technical debt?",
    "Which tech innovation has impressed you the most recently?",
    "Mac, Windows, or Linux for development?",
    "How do you feel about AI code generation tools?",
    "What's your opinion on microservices vs monoliths?",
    "What's your preferred testing strategy?",
    "Which tech book has influenced you the most?",
    "What's your favorite way to document code?",
    "What's your opinion on TypeScript vs JavaScript?",
    "What's your favorite browser dev tool feature?",
    "How do you approach learning a new framework or language?",
    "What's the most underrated technology in your opinion?",
    "What technology are you most excited to learn next?",
    "What's your favorite database technology and why?",
    "What's your approach to code refactoring?",
    "What's your favorite way to deploy applications?",
    "What's the most surprising performance optimization you've discovered?",
    "What's your favorite front-end framework and why?"
  ];

  const quickQuestions = [
    // Personal
    "What's the last book you read?",
    "Coffee or tea?",
    "Early bird or night owl?",
    "Favorite weekend activity?",
    "Dream vacation destination?",
    "What's your perfect breakfast?",
    "What superpower would you choose?",
    "What's your favorite movie genre?",
    "If you could master any skill instantly, what would it be?",
    "What's your go-to comfort food?",
    "What's your favorite season?",
    "What's your ideal way to spend a Sunday?",
    "What's the best gift you've ever received?",
    "What's your favorite childhood memory?",
    "If you could live anywhere, where would it be?",
    "What's your favorite type of music?",
    "What's the last show you binge-watched?",
    "What's your favorite way to exercise?",
    "What's your hidden talent?",
    "If you could have dinner with anyone, who would it be?",
    // Additional Personal Questions
    "What hobby would you take up if time and money weren't factors?",
    "What's your favorite board game or card game?",
    "What's the most beautiful place you've ever visited?",
    "What's a skill you've always wanted to learn?",
    "Mountains, beach, or city for vacation?",
    "What's your favorite holiday and why?",
    "What's the best piece of advice you've ever received?",
    "What's your favorite app on your phone?",
    "What's something you're looking forward to?",
    "What's the oldest item you own and still use?",
    "What was your first job?",
    "What's your favorite local restaurant?",
    "What talent or skill do you admire in others?",
    "What's your preferred way to commute?",
    "How do you relax after a stressful day?",
    "What's your favorite dessert?",
    "What fictional world would you most want to live in?",
    "What's a cause you're passionate about?",
    "What's your favorite podcast or YouTube channel?",
    "If you had a free month to travel, where would you go?",
    "What's your least favorite household chore?",
    "What's your favorite childhood game?",
    "Audiobooks, e-books, or physical books?",
    "What's your favorite quote or saying?",
    "What's the best concert or live performance you've attended?",
    "If you could have any animal as a pet, what would it be?",
    "What three items would you take to a deserted island?",
    "What's the best gift you've ever given someone?",
    "What's something you've done that you never thought you would?",
    "What's your favorite way to spend time outdoors?"
  ];

  const wouldYouRatherQuestions = [
    // Tech-themed
    "Would you rather only code in assembly or only use Excel formulas forever?",
    "Would you rather lose all your code comments or all your variable names?",
    "Would you rather have perfect documentation or perfect test coverage?",
    "Would you rather only use Windows 95 or only use a smartphone from 2010?",
    "Would you rather never use Stack Overflow again or never use Git again?",
    // Life-themed
    "Would you rather be able to read minds or be invisible?",
    "Would you rather travel 100 years into the future or 100 years into the past?",
    "Would you rather have unlimited money or unlimited time?",
    "Would you rather be fluent in all languages or be able to play all instruments?",
    "Would you rather never need to sleep or never need to eat?",
    // Work-themed
    "Would you rather work from home forever or work from office forever?",
    "Would you rather have unlimited coffee or unlimited snacks?",
    "Would you rather have perfect focus for 4 hours or good focus for 8 hours?",
    "Would you rather be the best debugger or the best architect?",
    "Would you rather lead a team of 100 or be an individual contributor forever?",
    // Additional Tech-themed
    "Would you rather only use command line interfaces or only use GUI applications?",
    "Would you rather debug someone else's code forever or write new code that no one else will ever see?",
    "Would you rather only use a keyboard or only use a mouse?",
    "Would you rather have to deploy on Fridays or do code reviews on Mondays?",
    "Would you rather work with a perfect AI assistant or the world's best human programmer?",
    "Would you rather fix bugs all day or write documentation all day?",
    "Would you rather only code on a smartphone or only on a 15-year-old computer?",
    "Would you rather attend daily 2-hour meetings or fix merge conflicts all day?",
    "Would you rather lose your entire codebase or your entire database?",
    "Would you rather have 100% unit test coverage that takes 2 hours to run or 50% coverage that runs in seconds?",
    // Additional Life-themed
    "Would you rather know when you'll die or how you'll die?",
    "Would you rather be 10 years younger with your current knowledge or 10 years older with perfect health?",
    "Would you rather be famous but disliked or unknown but well-loved?",
    "Would you rather always speak your mind or never reveal your true thoughts?",
    "Would you rather have the ability to see 1 minute into the future or be able to redo 1 minute from your past?",
    "Would you rather never be stuck in traffic again or never have to clean again?",
    "Would you rather live without the internet or without air conditioning/heating?",
    "Would you rather have free flights or free accommodation anywhere?",
    "Would you rather be able to talk to animals or speak all human languages?",
    "Would you rather have a photographic memory or the ability to forget anything you want?",
    // Additional Work-themed
    "Would you rather have a brilliant but difficult boss or a kind but mediocre boss?",
    "Would you rather be overpaid at a boring job or underpaid at an exciting job?",
    "Would you rather work four 10-hour days or five 8-hour days?",
    "Would you rather always be 10 minutes early or always be 10 minutes late?",
    "Would you rather work at your dream company with a long commute or an okay company that's very close to home?",
    "Would you rather have a job where you're constantly learning or one where you're the expert?",
    "Would you rather be the least skilled person on an amazing team or the most skilled on a mediocre team?",
    "Would you rather have a 20% raise with no title change or a prestigious title with no raise?",
    "Would you rather have a private office with no windows or an open desk with a great view?",
    "Would you rather have unlimited sick days but no vacation or unlimited vacation but no sick days?"
  ];

  const generateQuestion = () => {
    const questions = activeGame === 'trivia' ? triviaQuestions : quickQuestions;
    const randomIndex = Math.floor(Math.random() * questions.length);
    setQuestion(questions[randomIndex]);
  };

  const teamMembers = getTeamMembers();

  const [wordChainState, setWordChainState] = useState({
    currentWord: '',
    usedWords: new Set<string>(),
    lastLetter: '',
    playerTurn: 1,
    players: Math.max(2, teamMembers.length),
    useTeamMembers: true,
    error: ''
  });

  const handleWordChainSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const word = wordChainState.currentWord.toLowerCase().trim();
    
    if (wordChainState.usedWords.has(word)) {
      setWordChainState(prev => ({ ...prev, error: 'Word already used!' }));
      return;
    }

    if (wordChainState.lastLetter && word[0] !== wordChainState.lastLetter) {
      setWordChainState(prev => ({ 
        ...prev, 
        error: `Word must start with the letter "${wordChainState.lastLetter}"!` 
      }));
      return;
    }

    const totalPlayers = teamMembers.length > 0 ? teamMembers.length : wordChainState.players;
    
    setWordChainState(prev => ({
      ...prev,
      usedWords: new Set([...prev.usedWords, word]),
      lastLetter: word[word.length - 1],
      currentWord: '',
      playerTurn: prev.playerTurn === totalPlayers ? 1 : prev.playerTurn + 1,
      error: ''
    }));
  };

  const getCurrentPlayerName = () => {
    const totalPlayers = teamMembers.length > 0 ? teamMembers.length : wordChainState.players;
    const playerIndex = (wordChainState.playerTurn - 1) % totalPlayers;
    return teamMembers[playerIndex] || `Player ${wordChainState.playerTurn}`;
  };

  return (
    <div className="space-y-6">
      <ToolDescription 
        title="Ice Breakers & Team Building"
        description="Build stronger team relationships and improve communication with engaging ice breaker activities. These tools help break down barriers, encourage participation, and create psychological safety within your development team."
        benefits={[
          "Improve team communication and collaboration",
          "Build psychological safety and trust",
          "Encourage participation from all team members",
          "Reduce social anxiety in team settings",
          "Create memorable shared experiences"
        ]}
        howToUse="Choose from different ice breaker activities: tech trivia for technical teams, personal questions to learn about each other, or word games for creative thinking. Perfect for sprint retrospectives, team meetings, or remote team building sessions."
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
          Ice Breakers & Team Building
        </h2>

        <div className="flex flex-wrap gap-2 mb-6">
          {games.map((game) => (
            <button
              key={game.id}
              onClick={() => setActiveGame(game.id)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg
                ${
                  activeGame === game.id
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-200'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                }
              `}
            >
              <game.icon size={20} />
              <span>{game.name}</span>
            </button>
          ))}
        </div>

        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6">
          {(activeGame === 'trivia' || activeGame === 'questions') && (
            <div className="space-y-4">
              <button
                onClick={generateQuestion}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Generate Question
              </button>
              {question && (
                <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <p className="text-lg text-gray-800 dark:text-gray-200">{question}</p>
                </div>
              )}
            </div>
          )}

          {activeGame === 'wordchain' && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-2">Word Chain Game</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Each player must say a word that begins with the last letter of the previous word.
                </p>
                
                <div className="mb-4 space-y-4">
                  {teamMembers.length > 0 ? (
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck size={16} className="text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Playing with Team Members</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {teamMembers.map((member, index) => (
                          <span 
                            key={index} 
                            className={`text-xs px-2 py-1 rounded-full ${
                              index === (wordChainState.playerTurn - 1) % teamMembers.length
                                ? 'bg-blue-500 text-white' 
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {member}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        No team members added yet. Add team members in the Agile Tools section to play with your team.
                      </p>
                    </div>
                  )}
                </div>

                <form onSubmit={handleWordChainSubmit} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={wordChainState.currentWord}
                      onChange={(e) => setWordChainState(prev => ({
                        ...prev,
                        currentWord: e.target.value
                      }))}
                      placeholder={`${getCurrentPlayerName()}'s turn${
                        wordChainState.lastLetter ? ` (must start with '${wordChainState.lastLetter}')` : ''
                      }`}
                      className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                  {wordChainState.error && (
                    <p className="text-red-500 text-sm">{wordChainState.error}</p>
                  )}
                </form>

                <div className="mt-4">
                  <h4 className="font-medium mb-2">Used Words:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(wordChainState.usedWords).map((word, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeGame === 'wouldyou' && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  const randomIndex = Math.floor(Math.random() * wouldYouRatherQuestions.length);
                  setQuestion(wouldYouRatherQuestions[randomIndex]);
                }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Generate Question
              </button>
              {question && (
                <div className="mt-4 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <p className="text-lg text-gray-800 dark:text-gray-200">{question}</p>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <button className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-4 py-2 rounded-lg hover:bg-green-200 dark:hover:bg-green-800">
                      Option A
                    </button>
                    <button className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800">
                      Option B
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ad Space */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center shadow-sm">
        <p className="text-sm text-gray-500 dark:text-gray-400">Advertisement</p>
      </div>
    </div>
  );
};

export default IceBreakers;